// Suivi d'un paiement par sa référence : en attente du prestataire (relu toutes les cinq secondes,
// accès à la page du prestataire ou à sa simulation), confirmé (quittance, PDF à télécharger dès
// qu'il est produit), refusé ou expiré (motif, créance intacte, nouvelle tentative possible).
import { Bandeau, Bouton, EtatVide, LienBouton, classesBouton } from '@cc/ui';
import { Download, ExternalLink } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router';
import { messageErreur } from '../../../api/erreurs';
import { EtatRequete } from '../../../commun/EtatRequete';
import { dateHeure } from '../../../commun/format';
import { Bloc, EnTeteCitoyen, ExigeConnexion, TexteStatut } from '../commun';
import { telechargerQuittance, useMesCreances, useMesPaiements, useMesQuittances } from './api';
import { enAttente, LIBELLES_CANAL_PAIEMENT, libelleEtatPaiement, montant, tonEtatPaiement } from './logique';
import type { PaiementUsager, QuittanceUsager } from './types';
import styles from '../citoyen.module.css';

export const TEXTES_ETAT: Record<string, string> = {
  initie: 'Le paiement est initié : terminez-le sur la page du prestataire.',
  en_attente: 'Le prestataire n\'a pas encore confirmé le paiement. Cette page se met à jour toute seule.',
  confirme: 'Le paiement est confirmé et imputé sur votre créance ; la collectivité a délivré la quittance.',
  refuse: 'Le prestataire a refusé le paiement : rien n\'a été prélevé et votre créance est inchangée. Vous pouvez réessayer.',
  expire: 'Le délai du prestataire est écoulé sans confirmation : votre créance est inchangée. Vous pouvez réessayer.',
  annule: 'Ce paiement a été annulé par la collectivité ; sa quittance ne vaut plus.',
  rembourse: 'Ce paiement vous a été remboursé.',
};

/** Quittance du paiement : numéro, code et état viennent du paiement lui-même ; la liste des quittances apporte le PDF et la mention. */
function Quittance({ p, q }: { p: PaiementUsager; q: QuittanceUsager | null }) {
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const numero = q?.numero ?? p.quittance?.numero ?? '';
  const code = q?.code_verification ?? p.quittance?.code_verification ?? '';
  const etat = q?.etat ?? p.quittance?.etat ?? 'valide';
  return (
    <div className={styles.pile}>
      <dl className={styles.definitions}>
        <dt>Quittance</dt>
        <dd className={styles.mono}>{numero}</dd>
        <dt>Code de vérification</dt>
        <dd className={styles.mono}>{code}</dd>
        <dt>État</dt>
        <dd>
          <TexteStatut libelle={etat === 'valide' ? 'Valide' : `Annulée${q?.motif_annulation ? ` : ${q.motif_annulation}` : ''}`} ton={etat === 'valide' ? 'ok' : 'attention'} />
        </dd>
        {q?.mention_environnement ? (
          <>
            <dt>Mention</dt>
            <dd>{q.mention_environnement}</dd>
          </>
        ) : null}
      </dl>
      {q?.pdf_disponible ? (
        <Bouton
          variante="secondaire"
          icone={Download}
          chargement={enCours}
          onClick={() => {
            setEnCours(true);
            setErreur(null);
            telechargerQuittance(q.id, numero)
              .catch((e: unknown) => setErreur(messageErreur(e)))
              .finally(() => setEnCours(false));
          }}
        >
          Télécharger la quittance (PDF)
        </Bouton>
      ) : (
        <p className={styles.aide} role="status">
          Le PDF de la quittance est en préparation : il sera proposé ici dans quelques instants.
        </p>
      )}
      {erreur ? <Bandeau type="erreur">{erreur}</Bandeau> : null}
    </div>
  );
}

function Suivi({ p, urlPaiement }: { p: PaiementUsager; urlPaiement: string | null }) {
  const creances = useMesCreances();
  // La liste des quittances est relue toutes les cinq secondes tant que le PDF de celle du paiement n'est pas disponible.
  const [pdfAttendu, setPdfAttendu] = useState(true);
  const quittances = useMesQuittances((p.etat === 'confirme' || p.etat === 'annule') && pdfAttendu);
  const quittance = (quittances.data ?? []).find((q) => q.paiement_id === p.id) ?? null;
  useEffect(() => {
    setPdfAttendu(!quittance?.pdf_disponible);
  }, [quittance?.pdf_disponible]);
  const simulateur = p.prestataire === 'simulateur';
  const visees = (creances.data ?? []).filter((c) => p.creances_visees.includes(c.id));
  return (
    <>
      <Bloc etiquette={`Référence ${p.reference_serveur}`} titre={`${montant(p.montant)} · ${p.collectivite.nom}`}>
        <p className={styles.blocTexte}>
          <TexteStatut libelle={libelleEtatPaiement(p.etat)} ton={tonEtatPaiement(p.etat)} /> · {LIBELLES_CANAL_PAIEMENT[p.canal] ?? p.canal}
        </p>
        <p className={styles.blocTexte}>{TEXTES_ETAT[p.etat] ?? ''}</p>
        {p.motif_refus ? <p className={styles.blocTexte}>Motif du prestataire : {p.motif_refus}</p> : null}
        {enAttente(p) ? (
          <div className={styles.ligneBoutons}>
            {simulateur ? (
              <LienBouton href={`/citoyen/paiements/simulateur/${p.reference_serveur}`} variante="principal">
                Ouvrir le simulateur de paiement
              </LienBouton>
            ) : urlPaiement ? (
              <a className={classesBouton('principal')} href={urlPaiement} rel="noopener">
                Continuer chez le prestataire <ExternalLink aria-hidden width={14} height={14} />
              </a>
            ) : (
              <p className={styles.aide}>La page du prestataire n&apos;a pas été communiquée : reprenez le paiement depuis la créance.</p>
            )}
          </div>
        ) : null}
        {(p.etat === 'refuse' || p.etat === 'expire') && visees[0] ? (
          <LienBouton href={`/citoyen/paiements/creances/${visees[0].id}`} variante="principal">
            Réessayer le paiement
          </LienBouton>
        ) : null}
      </Bloc>
      {p.etat === 'confirme' || p.etat === 'annule' ? (
        <section className={styles.carte} aria-labelledby="quittance-titre">
          <h2 id="quittance-titre" className={styles.blocTitre}>
            Quittance
          </h2>
          {quittance || p.quittance ? <Quittance p={p} q={quittance} /> : <p className={styles.aide}>La quittance n&apos;est pas encore disponible.</p>}
        </section>
      ) : null}
      <section className={styles.carte} aria-labelledby="detail-paiement">
        <h2 id="detail-paiement" className={styles.blocTitre}>
          Détail
        </h2>
        <dl className={styles.definitions}>
          <dt>Initié le</dt>
          <dd>{dateHeure(p.initie_le)}</dd>
          {p.confirme_le ? (
            <>
              <dt>Confirmé le</dt>
              <dd>{dateHeure(p.confirme_le)}</dd>
            </>
          ) : null}
          {p.refuse_le ? (
            <>
              <dt>Refusé le</dt>
              <dd>{dateHeure(p.refuse_le)}</dd>
            </>
          ) : null}
          {p.reference_prestataire ? (
            <>
              <dt>Référence du prestataire</dt>
              <dd className={styles.mono}>{p.reference_prestataire}</dd>
            </>
          ) : null}
          <dt>Créances visées</dt>
          <dd>
            {visees.length === 0 ? `${p.creances_visees.length} créance${p.creances_visees.length > 1 ? 's' : ''}` : null}
            {visees.map((c) => (
              <span key={c.id}>
                <a href={`/citoyen/paiements/creances/${c.id}`}>{c.libelle}</a> ({c.numero}){' '}
              </span>
            ))}
          </dd>
        </dl>
      </section>
    </>
  );
}

export function PageSuiviPaiement() {
  const { reference = '' } = useParams();
  const location = useLocation();
  const urlPaiement = ((location.state as { url_paiement?: string | null } | null)?.url_paiement ?? null) as string | null;
  const paiements = useMesPaiements(true);
  const p = (paiements.data ?? []).find((x) => x.reference_serveur === reference) ?? null;
  return (
    <>
      <EnTeteCitoyen titre="Suivi du paiement" retour="/citoyen/paiements" libelleRetour="Paiements" surTitre={reference} />
      <ExigeConnexion titre="Connectez-vous pour suivre ce paiement">
        <EtatRequete chargement={paiements.isPending} erreur={paiements.error} onReessayer={() => void paiements.refetch()} fonction="Vos paiements" lot="L2" lignes={4}>
          {p ? (
            <Suivi p={p} urlPaiement={urlPaiement} />
          ) : (
            <EtatVide titre="Paiement introuvable" niveau={2}>
              <p>Aucun paiement de votre compte ne porte la référence {reference}.</p>
            </EtatVide>
          )}
        </EtatRequete>
      </ExigeConnexion>
    </>
  );
}
